import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  interest: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Request received. We'll be in touch shortly.");
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us at (435) 701-7171.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* FORM FIRST — top of page */}
      <section
        className="relative pt-40 pb-24 lg:pt-52 lg:pb-32 px-6 lg:px-10 overflow-hidden"
        data-testid="contact-form-section"
      >
        <div className="ambient-glow-blue" style={{ top: "-10%", right: "-20%", opacity: 0.35 }} />
        <div className="ambient-glow-navy" style={{ bottom: "-20%", left: "-10%", opacity: 0.45 }} />

        <div className="mx-auto max-w-[1400px] relative">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-[1px] w-16 bg-[color:var(--blue)]" />
            <div className="text-[11px] uppercase tracking-[0.4em] text-[color:var(--blue)]">
              Free Water Test Request
            </div>
          </div>

          <div className="grid grid-cols-12 gap-y-12 lg:gap-x-16 items-start">
            {/* Form (dominant) */}
            <div className="col-span-12 lg:col-span-8">
              <Reveal>
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="p-10 lg:p-16 bg-[color:var(--surface)] border border-[color:var(--border)] rounded-sm"
                    data-testid="contact-success"
                  >
                    <div className="text-[11px] uppercase tracking-[0.35em] text-[color:var(--blue)]">
                      Received
                    </div>
                    <h3 className="mt-4 font-display font-semibold text-4xl lg:text-5xl text-white leading-[1.05]">
                      Thank you.<br />
                      <span className="font-editorial italic font-normal text-[color:var(--blue)]">
                        We'll be in touch.
                      </span>
                    </h3>
                    <p className="mt-6 text-[15px] leading-[1.75] text-[color:var(--muted)] max-w-lg">
                      A confirmation is on its way to your inbox. A Vanguard specialist will call you shortly to schedule your free in-home water test.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSubmitted(false)}
                      className="btn-ghost mt-10"
                      data-testid="contact-reset"
                    >
                      Submit another request
                    </button>
                  </motion.div>
                ) : (
                  <form
                    onSubmit={submit}
                    className="p-8 lg:p-12 bg-[color:var(--surface)] border border-[color:var(--border)] rounded-sm"
                    data-testid="contact-form"
                  >
                    <div className="text-[11px] uppercase tracking-[0.35em] text-[color:var(--blue)] mb-8">
                      Free water test request
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
                      <div>
                        <label className="form-label" htmlFor="name">Name*</label>
                        <input
                          id="name"
                          className="form-input"
                          value={form.name}
                          onChange={update("name")}
                          placeholder="Your full name"
                          required
                          data-testid="form-name"
                        />
                      </div>
                      <div>
                        <label className="form-label" htmlFor="phone">Phone*</label>
                        <input
                          id="phone"
                          className="form-input"
                          value={form.phone}
                          onChange={update("phone")}
                          placeholder="(555) 555-5555"
                          required
                          data-testid="form-phone"
                        />
                      </div>
                      <div>
                        <label className="form-label" htmlFor="email">Email*</label>
                        <input
                          id="email"
                          type="email"
                          className="form-input"
                          value={form.email}
                          onChange={update("email")}
                          placeholder="you@example.com"
                          required
                          data-testid="form-email"
                        />
                      </div>
                      <div>
                        <label className="form-label" htmlFor="city">City</label>
                        <input
                          id="city"
                          className="form-input"
                          value={form.city}
                          onChange={update("city")}
                          placeholder="Logan, UT"
                          data-testid="form-city"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="form-label" htmlFor="address">Address</label>
                        <input
                          id="address"
                          className="form-input"
                          value={form.address}
                          onChange={update("address")}
                          placeholder="Street, City, State ZIP"
                          data-testid="form-address"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="form-label mb-3 block">I'm interested in</label>
                        <Select value={form.interest} onValueChange={update("interest")}>
                          <SelectTrigger
                            className="w-full bg-transparent border-0 border-b border-white/15 rounded-none px-0 pb-4 pt-2 h-auto text-white text-base focus:border-[color:var(--blue)] focus:ring-0"
                            data-testid="form-interest"
                          >
                            <SelectValue placeholder="Choose a system (optional)" />
                          </SelectTrigger>
                          <SelectContent className="bg-[color:var(--surface)] border-[color:var(--border)] text-white">
                            <SelectItem value="softener">Water Softener</SelectItem>
                            <SelectItem value="drinking">Drinking Water System</SelectItem>
                            <SelectItem value="filtration">Whole-Home Filtration</SelectItem>
                            <SelectItem value="well">Well / Problem Water</SelectItem>
                            <SelectItem value="service">Service / Repair</SelectItem>
                            <SelectItem value="unsure">Not sure — please advise</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="form-label" htmlFor="message">Message</label>
                        <textarea
                          id="message"
                          className="form-input resize-none"
                          rows={3}
                          value={form.message}
                          onChange={update("message")}
                          placeholder="Tell us about your water (well or city, hardness, taste, appliances)…"
                          data-testid="form-message"
                        />
                      </div>
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--muted)]">
                        We reply within 1 business day
                      </div>
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={submitting}
                        data-testid="form-submit"
                      >
                        {submitting ? "Sending…" : "Request Free Water Test"}
                        {!submitting && <ArrowUpRight className="h-4 w-4" />}
                      </button>
                    </div>
                  </form>
                )}
              </Reveal>
            </div>

            {/* Contact info sidebar */}
            <div className="col-span-12 lg:col-span-4">
              <Reveal delay={0.1}>
                <div className="text-[11px] uppercase tracking-[0.35em] text-[color:var(--muted)]">
                  Or reach us directly
                </div>

                <ul className="mt-8 space-y-8">
                  <li>
                    <div className="text-[10px] uppercase tracking-[0.35em] text-[color:var(--muted)] mb-2">Phone</div>
                    <a
                      href="tel:+14357017171"
                      className="group inline-flex items-center gap-3 font-display text-2xl text-white hover:text-[color:var(--blue)] transition-colors"
                      data-testid="contact-phone"
                    >
                      <Phone className="h-5 w-5 text-[color:var(--blue)]" />
                      (435) 701-7171
                    </a>
                  </li>
                  <li>
                    <div className="text-[10px] uppercase tracking-[0.35em] text-[color:var(--muted)] mb-2">Email</div>
                    <a
                      href="mailto:VanguardWaterSystems@gmail.com"
                      className="group inline-flex items-center gap-3 font-body text-base text-white hover:text-[color:var(--blue)] transition-colors break-all"
                      data-testid="contact-email"
                    >
                      <Mail className="h-4 w-4 text-[color:var(--blue)]" />
                      VanguardWaterSystems@gmail.com
                    </a>
                  </li>
                  <li>
                    <div className="text-[10px] uppercase tracking-[0.35em] text-[color:var(--muted)] mb-2">Studio</div>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=180+N+100+E+Logan+UT+84321"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-start gap-3 font-body text-base text-white/90 hover:text-[color:var(--blue)] transition-colors"
                      data-testid="contact-address"
                    >
                      <MapPin className="h-4 w-4 text-[color:var(--blue)] mt-1" />
                      <span>180 N 100 E<br />Logan, UT 84321</span>
                    </a>
                  </li>
                  <li>
                    <div className="text-[10px] uppercase tracking-[0.35em] text-[color:var(--muted)] mb-2">Service Area</div>
                    <div className="inline-flex items-start gap-3 font-body text-base text-white/90">
                      <MapPin className="h-4 w-4 text-[color:var(--blue)] mt-1" />
                      <span>Northern Utah<br />Southern Idaho</span>
                    </div>
                  </li>
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* "Let's test your water" hero — moved BELOW the form */}
      <section
        className="relative pt-24 pb-32 lg:pt-32 lg:pb-40 px-6 lg:px-10 overflow-hidden border-t border-[color:var(--border)]"
        data-testid="contact-hero-below"
      >
        <div className="ambient-glow-navy" style={{ top: "-30%", left: "10%", opacity: 0.5 }} />
        <div className="mx-auto max-w-[1400px] relative">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-[1px] w-16 bg-[color:var(--blue)]" />
            <div className="text-[11px] uppercase tracking-[0.4em] text-[color:var(--muted)]">
              Why book a test
            </div>
          </div>
          <h2 className="font-display font-semibold text-white text-[clamp(3rem,9vw,9rem)] leading-[0.9]">
            Let's test<br />
            <span className="font-editorial italic font-normal text-[color:var(--blue)]">
              your water.
            </span>
          </h2>
          <div className="mt-12 grid grid-cols-12">
            <p className="col-span-12 lg:col-span-6 lg:col-start-7 text-[16px] lg:text-[18px] leading-[1.75] text-[color:var(--muted)] font-body max-w-xl">
              A Vanguard specialist visits your home, runs a full water test right there, and shows you the results in plain English. No pressure, no obligation — just clarity about the water your family drinks and bathes in every day.
            </p>
          </div>
        </div>
      </section>
      <section className="pb-32 lg:pb-40 px-6 lg:px-10" data-testid="map-section">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-10 items-end">
            <div className="col-span-12 lg:col-span-6">
              <div className="text-[11px] uppercase tracking-[0.35em] text-[color:var(--blue)]">Find us</div>
              <h2 className="mt-4 font-display font-semibold text-[clamp(2rem,5vw,4rem)] leading-[0.95] text-white">
                Come by the<br />
                <span className="font-editorial italic font-normal text-[color:var(--blue)]">
                  Logan studio.
                </span>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8">
              <p className="text-[15px] leading-[1.7] text-[color:var(--muted)] font-body max-w-md">
                Stop in to see the systems in person or call to schedule your free in-home water test.
              </p>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=180+N+100+E+Logan+UT+84321"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost mt-6"
                data-testid="map-directions"
              >
                <MapPin className="h-4 w-4" /> Get Directions
              </a>
            </div>
          </div>

          <div
            className="relative overflow-hidden product-frame border border-[color:var(--border)]"
            style={{ aspectRatio: "16 / 9" }}
          >
            <iframe
              title="Vanguard Water Systems — 180 N 100 E, Logan UT"
              src="https://www.google.com/maps?q=180+N+100+E+Logan+UT+84321&output=embed"
              width="100%"
              height="100%"
              style={{
                border: 0,
                filter: "grayscale(0.6) contrast(1.05) brightness(0.85) invert(0.9) hue-rotate(180deg)",
              }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
